import { Configuration, DefaultApi, PortalApi } from '@/api-client/autogenerated';
import { API_URL } from '@/utils/env-setup';
import axios, { AxiosError } from 'axios';

export const resolveApiBasePath = (): string => {
  if (API_URL) return API_URL;
  if (typeof window !== 'undefined') {
    // In development with subdomains (e.g., portal.localhost:3000),
    // we need to route API calls to localhost:10000
    const hostname = window.location.hostname;
    if (hostname.includes('localhost')) {
      return 'http://localhost:10000';
    }
    return `${window.location.protocol}//${window.location.host}`;
  }
  return 'http://localhost:10000';
};

let ApiClient: DefaultApi;
let PortalApiClient: PortalApi;

export const getApiClient = () => {
  if (ApiClient) return ApiClient;

  const basePath = resolveApiBasePath();

  // Create axios instance with error interceptor
  const axiosInstance = axios.create({
    withCredentials: true,
    maxContentLength: 100 * 1024 * 1024, // 100MB
    maxBodyLength: 100 * 1024 * 1024, // 100MB
  });

  // Add request interceptor to append portal query parameter if present
  axiosInstance.interceptors.request.use((config) => {
    if (typeof window !== 'undefined') {
      // Check for portal query parameter first
      const urlParams = new URLSearchParams(window.location.search);
      let portalParam = urlParams.get('portal');

      // If no query parameter, check for subdomain in hostname
      if (!portalParam) {
        const hostname = window.location.hostname;
        const parts = hostname.split('.');

        // For localhost development, check if we have subdomain.localhost pattern
        if (hostname.includes('localhost') && parts.length >= 2 && parts[0] !== 'localhost') {
          portalParam = parts[0];
        }
        // Check if we're on a subdomain (not www, not just base domain)
        else if (parts.length >= 2 && parts[0] !== 'www' && !hostname.includes('azurewebsites')) {
          portalParam = parts[0];
        }
      }

      if (portalParam && config.url) {
        // Append portal parameter to request URL
        const separator = config.url.includes('?') ? '&' : '?';
        config.url = `${config.url}${separator}portal=${encodeURIComponent(portalParam)}`;
      }
    }
    return config;
  });

  // Add response interceptor to handle errors properly
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (error.response?.data && typeof error.response.data === 'object') {
        const responseData = error.response.data as any;
        // Extract error message from response body
        const message = responseData.message || responseData.error || responseData.details;
        if (message) {
          // Create a new error with the extracted message
          const customError = new Error(message);
          customError.name = error.name;
          throw customError;
        }
      }
      throw error;
    },
  );

  const client = new DefaultApi(
    new Configuration({
      basePath,
      baseOptions: { withCredentials: true },
    }),
    basePath,
    axiosInstance,
  );

  ApiClient = client;

  return client;
};

export const getPortalApiClient = () => {
  if (PortalApiClient) return PortalApiClient;

  const basePath = resolveApiBasePath();

  // Create axios instance with error interceptor
  const axiosInstance = axios.create({
    withCredentials: true,
    maxContentLength: 100 * 1024 * 1024, // 100MB
    maxBodyLength: 100 * 1024 * 1024, // 100MB
  });

  // Add request interceptor to append portal query parameter if present
  axiosInstance.interceptors.request.use((config) => {
    if (typeof window !== 'undefined') {
      // Check for portal query parameter first
      const urlParams = new URLSearchParams(window.location.search);
      let portalParam = urlParams.get('portal');

      // If no query parameter, check for subdomain in hostname
      if (!portalParam) {
        const hostname = window.location.hostname;
        const parts = hostname.split('.');

        // For localhost development, check if we have subdomain.localhost pattern
        if (hostname.includes('localhost') && parts.length >= 2 && parts[0] !== 'localhost') {
          portalParam = parts[0];
        }
        // Check if we're on a subdomain (not www, not just base domain)
        else if (parts.length >= 2 && parts[0] !== 'www' && !hostname.includes('azurewebsites')) {
          portalParam = parts[0];
        }
      }

      if (portalParam && config.url) {
        // Append portal parameter to request URL
        const separator = config.url.includes('?') ? '&' : '?';
        config.url = `${config.url}${separator}portal=${encodeURIComponent(portalParam)}`;
      }
    }
    return config;
  });

  // Add response interceptor to handle errors properly
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (error.response?.data && typeof error.response.data === 'object') {
        const responseData = error.response.data as any;
        // Extract error message from response body
        const message = responseData.message || responseData.error || responseData.details;
        if (message) {
          // Create a new error with the extracted message
          const customError = new Error(message);
          customError.name = error.name;
          throw customError;
        }
      }
      throw error;
    },
  );

  const client = new PortalApi(
    new Configuration({
      basePath,
      baseOptions: { withCredentials: true },
    }),
    basePath,
    axiosInstance,
  );

  PortalApiClient = client;

  return client;
};
